/**
 * @ken/module-suppliers — frontend entry point.
 *
 * Mirrors @ken/module-products + @ken/module-customers shape.
 *
 * 2026-06-15 (story_page_archetype_rollout): the in-list detail Sheet is
 * RETIRED for suppliers in favour of the full-page STORY route
 * (ADR-0081/0082, PARTY archetype). Clicking a supplier row navigates to
 * `suppliers/$id/overview`; the parent `suppliers/$id` renders the
 * StoryPageDetail shell + routed tab children (overview / related / notes)
 * with an index redirect to overview. Mirrors the customers story rollout.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { SupplierStorySearch } from "./routes/story/supplier-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadSupplierStoryTabs = () => import("./routes/story/supplier-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const SupplierCreatePage = lazyScreen(
  () => import("./routes/supplier-create"),
  "SupplierCreatePage",
);
const SupplierListPage = lazyScreen(
  () => import("./routes/supplier-list"),
  "SupplierListPage",
);
const SupplierStoryDetail = lazyScreen(
  () => import("./routes/story/supplier-story-detail"),
  "SupplierStoryDetail",
);
const SupplierActivityRoute = lazyScreen(
  loadSupplierStoryTabs,
  "SupplierActivityRoute",
);
const SupplierNotesRoute = lazyScreen(
  loadSupplierStoryTabs,
  "SupplierNotesRoute",
);
const SupplierOverviewRoute = lazyScreen(
  loadSupplierStoryTabs,
  "SupplierOverviewRoute",
);
const SupplierRelatedRoute = lazyScreen(
  loadSupplierStoryTabs,
  "SupplierRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "suppliers",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): SupplierListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: SupplierListPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "suppliers/new",
    component: SupplierCreatePage,
  });

  // ── Story-page detail (ADR-0081): parent suppliers/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "suppliers/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): SupplierStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: SupplierStoryDetail,
  });

  // Index redirect suppliers/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/suppliers/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: SupplierOverviewRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: SupplierRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: SupplierNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: SupplierActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, relatedRoute, notesRoute, activityRoute],
  );

  return [listRoute, createRouteEntry, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface SupplierListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
